import type { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuthStore } from '@/stores/useAuthStore';
import { useAuth } from '@/auth/useAuth';
import { LoadingSpinner } from '@/components/shared/LoadingSpinner';
import { Button } from '@/components/ui/button';

export function AuthGuard({ children }: { children: ReactNode }) {
  const { isAuthenticated, isLoading, noProfile, manager } = useAuthStore();
  const { logout } = useAuth();

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  // No session at all → send to login.
  if (!isAuthenticated && !noProfile) {
    return <Navigate to="/login" replace />;
  }

  // Authenticated identity but no usable access: no profile row, or a profile with
  // no organizations that isn't a super admin (super admins see every org, including
  // when none exist yet so they can create the first one).
  const hasAccess =
    manager != null && (manager.is_super_admin || manager.organization_ids.length > 0);
  if (!hasAccess) {
    return (
      <div className="flex h-screen flex-col items-center justify-center gap-4 px-6 text-center">
        <h1 className="text-2xl font-bold text-brand-black">Access not provisioned</h1>
        <p className="max-w-md text-gray-600">
          Your account isn&apos;t set up with access to an organization yet. Contact your
          administrator to be granted access.
        </p>
        <Button variant="outline" onClick={logout}>
          Sign out
        </Button>
      </div>
    );
  }

  return <>{children}</>;
}
